import { z } from 'zod';
import { addDaysIso, zonedDate, zonedParts, zonedToUtc } from './time.js';
import { formatClock } from './zones.js';
/**
 * Calendars in My day (docs/adr/0106-calendars-in-my-day.md): a person's Google, Outlook and iCloud events beside
 * their to-dos, read-only. The schemas the API and the apps share, and the pure helpers the web and the phone use to
 * lay the agenda out (so both read "Free until 2:30 pm" the same way). Nothing here ever writes to a calendar.
 */
export const CalendarProvider = z.enum(['google', 'microsoft', 'icloud']);
export const OAuthCalendarProvider = z.enum(['google', 'microsoft']);
export const CALENDAR_PROVIDER_LABELS = {
    google: 'Google Calendar',
    microsoft: 'Outlook / Microsoft 365',
    icloud: 'iCloud Calendar',
};
/** How many days My day shows: today and the next six. */
export const CALENDAR_DAYS = 7;
export const CALENDAR_MAX_DAYS = 14;
/** The web polls this often while My day is open; the API caches for the same time. */
export const CALENDAR_REFRESH_MS = 5 * 60_000;
const HexColor = z.string().regex(/^#[0-9a-f]{6}$/);
export const CalendarChoice = z.object({
    id: z.uuid(),
    name: z.string(),
    color: HexColor,
    primary: z.boolean(),
    selected: z.boolean(),
});
export const CalendarConnectionStatus = z.enum(['connected', 'needs_reconnect']);
/** One connected account. Never carries a token or a password. */
export const CalendarConnection = z.object({
    id: z.uuid(),
    provider: CalendarProvider,
    email: z.string(),
    status: CalendarConnectionStatus,
    connectedAt: z.string(),
    calendars: z.array(CalendarChoice),
});
export const CalendarSettings = z.object({
    connections: z.array(CalendarConnection),
    /** Which providers this server can connect (Google and Microsoft need the sign-in apps of docs/adr/0081). */
    providers: z.object({ google: z.boolean(), microsoft: z.boolean(), icloud: z.boolean() }),
});
export const StartCalendarConnectInput = z.object({
    /** Reconnect this connection (it needs reconnecting): the provider must answer with the same account. */
    reconnectId: z.uuid().optional(),
});
export const StartCalendarConnectResponse = z.object({ url: z.string() });
export const CompleteCalendarConnectInput = z.object({
    provider: OAuthCalendarProvider,
    code: z.string().min(1).max(2048),
    state: z.string().min(16).max(256),
});
/** Apple's app-specific passwords: four groups of four lower-case letters. */
export const APP_SPECIFIC_PASSWORD = /^[a-z]{4}-[a-z]{4}-[a-z]{4}-[a-z]{4}$/;
export const ConnectIcloudInput = z.object({
    appleId: z.string().trim().toLowerCase().max(254).regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'Enter the email address of your Apple ID'),
    appPassword: z
        .string()
        .trim()
        .toLowerCase()
        .regex(APP_SPECIFIC_PASSWORD, 'Use an app-specific password from appleid.apple.com: four groups of four letters, like xxxx-xxxx-xxxx-xxxx'),
    reconnectId: z.uuid().optional(),
});
export const ChooseCalendarsInput = z.object({ selectedIds: z.array(z.uuid()).max(200) });
/** The provider's answer at our callback (any of these may be missing). */
export const CalendarCallbackQuery = z.object({
    code: z.string().max(4096).optional(),
    state: z.string().max(512).optional(),
    error: z.string().max(200).optional(),
});
export const CalendarCallbackParams = z.object({});
export const CalendarRedirect = z.object({});
export const CalendarEventsQuery = z.object({
    days: z.coerce.number().int().min(1).max(CALENDAR_MAX_DAYS).default(CALENDAR_DAYS),
    refresh: z.enum(['true', 'false']).optional(),
});
export const CalendarEvent = z.object({
    /** Opaque and stable for the fetch: a hash, never the provider's id. */
    id: z.string(),
    connectionId: z.uuid(),
    provider: CalendarProvider,
    calendarName: z.string(),
    color: HexColor,
    title: z.string(),
    location: z.string().nullable(),
    /** Instants (ISO). For an all-day event: local midnight of its first day, and of the day after its last. */
    start: z.string(),
    end: z.string(),
    allDay: z.boolean(),
    /** First and last local day (YYYY-MM-DD, inclusive) in the person's time zone. */
    startDate: z.string(),
    endDate: z.string(),
    /** Free / transparent: shown lighter, never busy. */
    free: z.boolean(),
    /** An https meeting link (Meet, Teams, a URL location), or null. */
    joinUrl: z.string().nullable(),
});
export const CalendarFetchState = z.enum(['ok', 'needs_reconnect', 'unavailable']);
export const CalendarAgenda = z.object({
    timeZone: z.string(),
    today: z.string(),
    /** The days asked for, in order, starting today. */
    days: z.array(z.string()),
    events: z.array(CalendarEvent),
    /** How each connection did in this fetch. One failing provider never fails the others. */
    connections: z.array(z.object({ id: z.uuid(), provider: CalendarProvider, email: z.string(), state: CalendarFetchState })),
    fetchedAt: z.string(),
});
/** What the person's export holds about their calendars: accounts and choices, never credentials or events. */
export const CalendarExportRow = z.object({
    provider: CalendarProvider,
    email: z.string(),
    status: CalendarConnectionStatus,
    connectedAt: z.string(),
    calendars: z.array(z.object({ name: z.string(), color: HexColor, selected: z.boolean() })),
});
// --- Pure helpers (web and phone) ----------------------------------------------------------------
/** All-day events first, then by start, then by title. */
export function sortEvents(events) {
    return [...events].sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.localeCompare(b.start) || a.title.localeCompare(b.title));
}
/** The events on one local day, sorted. */
export function eventsOnDay(events, date) {
    return sortEvents(events.filter((e) => e.startDate <= date && e.endDate >= date));
}
/** "9:30 – 10:00 am", "11:30 am – 1:00 pm", "All day"; a time on a day it doesn't start or end on reads "until 2:00 pm". */
export function formatEventTime(e, date, timeZone) {
    if (e.allDay)
        return 'All day';
    const s = new Date(e.start);
    const en = new Date(e.end);
    const startsToday = e.startDate === date;
    const endsToday = e.endDate === date;
    if (!startsToday && !endsToday)
        return 'All day';
    if (!startsToday)
        return `until ${formatClock(en, timeZone)}`;
    if (!endsToday)
        return `from ${formatClock(s, timeZone)}`;
    const a = formatClock(s, timeZone);
    const b = formatClock(en, timeZone);
    if (a === b)
        return a;
    const [aTime, aHalf] = a.split(' ');
    const [, bHalf] = b.split(' ');
    return aHalf === bHalf ? `${aTime} – ${b}` : `${a} – ${b}`;
}
/** Busy times on one local day: timed events that aren't free, clipped to the day and merged. Only times, never titles. */
export function busyIntervals(events, date, timeZone) {
    const [y, m, d] = date.split('-').map(Number);
    const dayStart = zonedToUtc(y, m, d, 0, 0, timeZone).getTime();
    const [ny, nm, nd] = addDaysIso(date, 1).split('-').map(Number);
    const dayEnd = zonedToUtc(ny, nm, nd, 0, 0, timeZone).getTime();
    const spans = events
        .filter((e) => !e.allDay && !e.free)
        .map((e) => [Math.max(Date.parse(e.start), dayStart), Math.min(Date.parse(e.end), dayEnd)])
        .filter(([s, e]) => e > s)
        .sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const [s, e] of spans) {
        const last = merged[merged.length - 1];
        if (last && s <= last[1])
            last[1] = Math.max(last[1], e);
        else
            merged.push([s, e]);
    }
    return merged.map(([s, e]) => ({ start: new Date(s).toISOString(), end: new Date(e).toISOString() }));
}
/**
 * The small hint under the agenda: "In a meeting until 3:00 pm", "Free until 2:30 pm", "No more meetings today".
 * Null when today has no timed meetings at all.
 */
export function freeHint(events, now, timeZone) {
    const today = zonedDate(now, timeZone);
    const busy = busyIntervals(events, today, timeZone);
    if (!busy.length)
        return null;
    const t = now.getTime();
    const current = busy.find((b) => Date.parse(b.start) <= t && Date.parse(b.end) > t);
    if (current) {
        const end = new Date(current.end);
        return zonedDate(end, timeZone) === today ? `In a meeting until ${formatClock(end, timeZone)}` : 'In meetings for the rest of the day';
    }
    const next = busy.find((b) => Date.parse(b.start) > t);
    if (next)
        return `Free until ${formatClock(new Date(next.start), timeZone)}`;
    return 'No more meetings today';
}
/**
 * Minutes free for work between now and the end of today's working hours, after the busy times. Plan my day uses it
 * (docs/adr/0106) so it never proposes more than fits.
 */
export function freeMinutesLeft(busy, now, timeZone, hours) {
    const p = zonedParts(now, timeZone);
    const at = (hhmm) => {
        const [h, m] = hhmm.split(':').map(Number);
        return zonedToUtc(p.year, p.month, p.day, h, m, timeZone).getTime();
    };
    const start = Math.max(now.getTime(), at(hours.start));
    const end = at(hours.end) > at(hours.start) ? at(hours.end) : zonedToUtc(p.year, p.month, p.day, 23, 59, timeZone).getTime();
    if (end <= start)
        return 0;
    let busyMs = 0;
    for (const b of busy) {
        const s = Math.max(Date.parse(b.start), start);
        const e = Math.min(Date.parse(b.end), end);
        if (e > s)
            busyMs += e - s;
    }
    return Math.max(0, Math.floor((end - start - busyMs) / 60_000));
}
/** Default length of a task with no estimate, for Plan my day. */
export const PLAN_DEFAULT_TASK_MINUTES = 30;
/** Keeps the plan items, in order, that fit in `freeMinutes` (a task with no estimate counts 30 minutes). */
export function fitPlanToFreeTime(items, freeMinutes) {
    const out = [];
    let left = freeMinutes;
    for (const i of items) {
        const need = i.minutes && i.minutes > 0 ? i.minutes : PLAN_DEFAULT_TASK_MINUTES;
        if (need > left)
            continue;
        left -= need;
        out.push(i);
    }
    return out;
}
/** Only an https link may be a "Join" link: https, a plain host (no user info), no spaces or control characters. */
export function safeJoinUrl(raw) {
    if (!raw)
        return null;
    const s = raw.trim();
    if (s.length > 2048 || /[\s\u0000-\u001f\u007f<>"]/.test(s))
        return null;
    const m = /^https:\/\/([^/?#]+)([/?#].*)?$/i.exec(s);
    if (!m || m[1].includes('@') || !/^[a-z0-9.-]+(:\d{1,5})?$/i.test(m[1]) || !m[1].includes('.'))
        return null;
    return `https://${m[1].toLowerCase()}${m[2] ?? '/'}`;
}
//# sourceMappingURL=calendars.js.map